import { useState } from 'react';
import { Button, Stack } from '@mui/material';
import { MouseEvent } from 'react';
import { OrgTypeWithRole } from 'src/@types/org';
import { Role } from 'src/@types/role';
import { InviteStatus } from 'src/@types/userOrg';
import { useUpdateCurrentUserOrgStatus } from 'src/hooks/query/userHooks';
import OrgSwitcherRowBase from './OrgSwitcherRowBase';

type PendingOrgInviteRowProps = {
  org: OrgTypeWithRole;
  onClose?: () => void;
  onSelectOrg: (org: OrgTypeWithRole, role: Role | undefined) => void;
};

export default function PendingOrgInviteRow({
  org,
  onClose,
  onSelectOrg,
}: PendingOrgInviteRowProps) {
  const { mutate: updateCurrentUserOrgStatus, isPending } = useUpdateCurrentUserOrgStatus(
    org.id || ''
  );

  const [rejectionInProgress, setRejectionInProgress] = useState(false);
  const [acceptanceInProgress, setAcceptanceInProgress] = useState(false);

  const handleAcceptInvite = () => {
    if (!org.id) return;
    setAcceptanceInProgress(true);

    updateCurrentUserOrgStatus(
      { data: { invite_status: InviteStatus.Accepted } },
      {
        onSuccess: () => {
          onClose?.();
          onSelectOrg(org, org.user_org_role);
          setAcceptanceInProgress(false);
        },
        onError: () => {
          setAcceptanceInProgress(false);
        },
      }
    );
  };

  const handleRejectInvite = () => {
    if (!org.id) return;
    setRejectionInProgress(true);

    updateCurrentUserOrgStatus(
      { data: { invite_status: InviteStatus.Rejected } },
      {
        onSuccess: () => {
          setRejectionInProgress(false);
        },
        onError: () => {
          setRejectionInProgress(false);
        },
      }
    );
  };

  const handleAcceptButtonClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    handleAcceptInvite();
  };

  const handleRejectButtonClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    handleRejectInvite();
  };

  return (
    <OrgSwitcherRowBase
      org={org}
      actions={
        <Stack direction="row" spacing={1.5}>
          <Button
            variant="soft"
            color="error"
            onClick={handleRejectButtonClick}
            disabled={!org.id || acceptanceInProgress}
            loading={rejectionInProgress && isPending}
            sx={{ borderRadius: 999 }}
          >
            Reject
          </Button>
          <Button
            variant="soft"
            color="primary"
            onClick={handleAcceptButtonClick}
            disabled={!org.id || rejectionInProgress}
            loading={acceptanceInProgress && isPending}
            sx={{ borderRadius: 999 }}
          >
            Accept Invite
          </Button>
        </Stack>
      }
    />
  );
}
